import { sessionTools } from './utils/sessionTools';
import {
  GooglePlacesLoaderProvider,
  firebaseAuth,
  gateNames,
  isSaasAccount,
  useGate,
  isSaasCredentialingAccount,
  isSaasCredentialingOnlyAccount,
} from '@mp';
import { MfaIdentityVerificationScreen } from '@mp/pages/multiFactorAuthentication/MfaIdentityVerificationScreen.web';
import { MfaSmsCodeEntryScreen } from '@mp/pages/multiFactorAuthentication/MfaSmsCodeEntryScreen.web';
import { useAnalytics, useConfigurationContext } from '@medely/ui-kit';

import { Head } from '@medely/web-components';
import { useDeviceInfo } from '@medely/web-components/hooks';
import { useQueryClient } from '@tanstack/react-query';
import { useInviteRedirect } from '@src/components/auth/AcceptInvite/useInviteRedirect';
import MobileAuthLogin from '@src/components/auth/MobileAuthLogin';
import CenteredLoader from '@src/components/CenteredLoader';
import LocationRedirect from '@src/components/LocationRedirect';
import { WebViews } from '@src/components/WebViews/WebViews';
import configKeys from '@mp/config';
import { pageTitles } from '@src/constants/pageTitles';
import useCurrentUser from '@src/hooks/useCurrentUser';
import useHandleError from '@src/hooks/useHandleError';
import useOnboarding from '@src/hooks/useOnboarding';
import moment from 'moment';
import React, { Suspense, lazy, useEffect } from 'react';
import { Navigate, Route, Routes, useNavigate, useLocation } from 'react-router-dom';
import { BooleanParam, StringParam, useQueryParam, useQueryParams } from 'use-query-params';
import { createRedirectSearch } from '@src/utils/createRedirectSearch';
import ProView from 'View';
import { multiFactor } from 'firebase/auth';
import { useFeatureGateContext } from './context/featureGateContext';
import { persistOverrideRedirectIfPresent } from './utils/persistOverrideRedirect';

type LazyRetryFunction = <T>(componentImport: () => Promise<T>, name: string) => Promise<T>;

// a function to retry loading a chunk to avoid chunk load error for out of date code
const lazyRetry: LazyRetryFunction = (componentImport, name) => {
  return new Promise((resolve, reject) => {
    // check if the window has already been refreshed
    const hasRefreshed = JSON.parse(
      window.sessionStorage.getItem(`retry-${name}-refreshed`) || 'false',
    );
    // try to import the component
    componentImport()
      .then((component) => {
        window.sessionStorage.setItem(`retry-${name}-refreshed`, 'false'); // success so reset the refresh
        resolve(component);
      })
      // if import fails, refresh the page automatically
      .catch((error) => {
        if (!hasRefreshed) {
          // not been refreshed yet
          window.sessionStorage.setItem(`retry-${name}-refreshed`, 'true'); // we are now going to refresh
          return window.location.reload(); // refresh the page
        }
        reject(error); // Default error behaviour as already tried refresh
      });
  });
};

const Login = lazy(() =>
  lazyRetry(
    () =>
      import('components/auth/index.ts').then(({ Login }) => ({
        default: Login,
      })),
    'login',
  ),
);
const SignUp = lazy(() =>
  lazyRetry(
    () =>
      import('components/auth/index.ts').then(({ SignUp }) => ({
        default: SignUp,
      })),
    'sign-up',
  ),
);

const AcceptInvite = lazy(() =>
  lazyRetry(
    () =>
      import('components/auth/index.ts').then(({ AcceptInvite }) => ({
        default: AcceptInvite,
      })),
    'invite',
  ),
);

const Create = lazy(() =>
  lazyRetry(
    () =>
      import('components/auth/index.ts').then(({ Create }) => ({
        default: Create,
      })),
    'create',
  ),
);

const SetPassword = lazy(() =>
  lazyRetry(
    () =>
      import('components/auth/index.ts').then(({ SetPassword }) => ({
        default: SetPassword,
      })),
    'set-password',
  ),
);

const ResetPassword = lazy(() =>
  lazyRetry(
    () =>
      import('components/auth/index.ts').then(({ ResetPassword }) => ({
        default: ResetPassword,
      })),
    'reset-password',
  ),
);
const ReferenceGateway = lazy(() =>
  lazyRetry(
    () =>
      import('routes/index.ts').then(({ ReferenceGateway }) => ({
        default: ReferenceGateway,
      })),
    'reference',
  ),
);
const ApplicationRoot = lazy(() =>
  lazyRetry(
    () =>
      import('routes/index.ts').then(({ ApplicationRoot }) => ({
        default: ApplicationRoot,
      })),
    'application',
  ),
);

const AssignmentsRoot = lazy(() =>
  lazyRetry(
    () =>
      import('routes/index.ts').then(({ AssignmentsRoot }) => ({
        default: AssignmentsRoot,
      })),
    'assignments',
  ),
);

const AvailableRoot = lazy(() =>
  lazyRetry(
    () =>
      import('routes/index.ts').then(({ AvailableRoot }) => ({
        default: AvailableRoot,
      })),
    'available',
  ),
);

const CredentialsRoot = lazy(() =>
  lazyRetry(
    () =>
      import('routes/index.ts').then(({ CredentialsRoot }) => ({
        default: CredentialsRoot,
      })),
    'credentials',
  ),
);

const MyAssignmentsRoot = lazy(() =>
  lazyRetry(
    () =>
      import('routes/index.ts').then(({ MyAssignmentsRoot }) => ({
        default: MyAssignmentsRoot,
      })),
    'my-assignments',
  ),
);

const EarningsRoot = lazy(() =>
  lazyRetry(
    () =>
      import('routes/index.ts').then(({ EarningsRoot }) => ({
        default: EarningsRoot,
      })),
    'earnings',
  ),
);

const JobsRoot = lazy(() =>
  lazyRetry(
    () =>
      import('routes/index.ts').then(({ JobsRoot }) => ({
        default: JobsRoot,
      })),
    'jobs',
  ),
);

const ProfileRoot = lazy(() =>
  lazyRetry(
    () =>
      import('routes/index.ts').then(({ ProfileRoot }) => ({
        default: ProfileRoot,
      })),
    'profile',
  ),
);

const ScheduleRoot = lazy(() =>
  lazyRetry(
    () =>
      import('routes/index.ts').then(({ ScheduleRoot }) => ({
        default: ScheduleRoot,
      })),
    'schedule',
  ),
);

const TimecardsRoot = lazy(() =>
  lazyRetry(
    () =>
      import('routes/index.ts').then(({ TimecardsRoot }) => ({
        default: TimecardsRoot,
      })),
    'timecards',
  ),
);

const TodayRoot = lazy(() =>
  lazyRetry(
    () =>
      import('routes/index.ts').then(({ TodayRoot }) => ({
        default: TodayRoot,
      })),
    'today',
  ),
);

const SettingsRoot = lazy(() =>
  lazyRetry(
    () =>
      import('routes/index.ts').then(({ SettingsRoot }) => ({
        default: SettingsRoot,
      })),
    'settings',
  ),
);

const OnboardingRoot = lazy(() =>
  lazyRetry(
    () =>
      import('routes/index.ts').then(({ OnboardingRoot }) => ({
        default: OnboardingRoot,
      })),
    'onboarding',
  ),
);

const ProPoolRoot = lazy(() =>
  lazyRetry(
    () =>
      import('routes/index.ts').then(({ ProPoolRoot }) => ({
        default: ProPoolRoot,
      })),
    'pro-pool',
  ),
);

const ShiftSwapsRoot = lazy(() =>
  lazyRetry(
    () =>
      import('routes/index.ts').then(({ ShiftSwapsRoot }) => ({
        default: ShiftSwapsRoot,
      })),
    'shift-swaps',
  ),
);

// Wrapper component to pass location to LocationRedirect in v6
const LocationRedirectWrapper = ({ host }: { host: string }) => {
  const location = useLocation();
  return <LocationRedirect host={host} location={location} />;
};

// Wrapper for timesheets route conditional logic
const TimesheetsRouteWrapper = ({
  currentUser,
  children,
}: {
  currentUser: any;
  children: React.ReactNode;
}) => {
  if (isSaasAccount(currentUser)) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
};

const AppRouter = () => {
  const config = useConfigurationContext();
  const { isLoading: loadingUser, currentUser, isApplicant } = useCurrentUser();
  const disableSegmentIntercomEvents = useGate(gateNames.disableSegmentIntercomEvents);
  const {
    showOnboarding,
    hasAnOnboardedPosition,
    isAssignmentOnboardingRequired,
    assignmentsMissingConditions,
  } = useOnboarding();

  const { isLoadingStatsig } = useFeatureGateContext();

  const url = new URL(window.location.href);
  const search = createRedirectSearch(url);

  const [{ webView, mobileToken }] = useQueryParams({
    mobileToken: StringParam,
    webView: BooleanParam,
  });

  const urlHasMobileAuthToken = !!mobileToken;

  const onError = useHandleError();
  const queryClient = useQueryClient();
  queryClient.setDefaultOptions({
    queries: {
      refetchOnWindowFocus: false,
      retry: false,
      // queries going stale was messing up our multi-page forms
      // we can change this to something more aggressive, but we need
      // to be very careful about queries that populate form options
      staleTime: Infinity,
      onError,
    },
    mutations: {
      onMutate: (context) => context,
      onSettled: () => {
        queryClient.invalidateQueries();
      },
      onError,
    },
  });

  const analytics = useAnalytics();
  const deviceInfo = useDeviceInfo();
  const navigate = useNavigate();
  const location = useLocation();

  const professionalType = (() => {
    if (currentUser?.company_id) {
      return 'irp';
    }
    if (currentUser?.agency_id) {
      return 'agency';
    }
    return 'marketplace';
  })();

  useEffect(() => {
    sessionTools.recordActivity(() => {
      analytics.track(
        {
          context: 'Session',
          action: 'Started',
          session_access_type: 'web',
          pro_type: professionalType,
        },
        {},
        {
          integrations: { Intercom: disableSegmentIntercomEvents ? false : true },
        },
      );
    });
  }, [location.pathname, location.search]);

  useEffect(() => {
    analytics.page('Professionals', location.pathname, { pro_type: professionalType });
  }, [location.pathname]);

  const followingQualificationRedirect = !!useQueryParam('credential', StringParam)[0];
  const ehrModalRedirect = useQueryParam('ehrModal', StringParam)[0];
  const [overrideRedirectParam] = useQueryParam('override_redirect', StringParam);

  const initialRedirectForOnboarding = () => {
    persistOverrideRedirectIfPresent(overrideRedirectParam);

    // PRO-4564 - Allow onboarding redirect when coming from mobile to complete application
    const urlParams = new URLSearchParams(location.search);
    const redirectParam = urlParams.get('redirect');
    const shouldCheckOnboardingForMobile = mobileToken && redirectParam === 'application';

    if (
      currentUser &&
      currentUser.professional?.status === 'active' &&
      !webView &&
      (!mobileToken || shouldCheckOnboardingForMobile) &&
      !followingQualificationRedirect &&
      ehrModalRedirect !== 'open' &&
      !location.pathname.includes('/mfa-entry') &&
      !location.pathname.includes('/referrals')
    ) {
      if (
        showOnboarding &&
        !hasAnOnboardedPosition &&
        !location.pathname.includes('/onboarding') &&
        !location.pathname.startsWith('/available') &&
        !location.pathname.startsWith('/assignments')
      ) {
        navigate('/onboarding');
      } else if (
        !showOnboarding &&
        isSaasCredentialingOnlyAccount(currentUser) &&
        !location.pathname.includes('/credentials')
      ) {
        navigate('/credentials');
      } else if (isAssignmentOnboardingRequired && !location.pathname.includes('/onboarding')) {
        navigate(`/assignments/${assignmentsMissingConditions[0]}/onboarding`);
      }
    }
  };
  useEffect(initialRedirectForOnboarding, [
    currentUser?.id,
    showOnboarding,
    assignmentsMissingConditions.length,
  ]);
  useInviteRedirect();

  const identifyUser = () => {
    if (currentUser) {
      analytics.identify(
        currentUser.id.toString(),
        {
          name: currentUser.name,
          email: currentUser.email,
          hide_default_launcher: true,
          status: currentUser.professional?.status,
          application_status: currentUser.professional?.application_status,
          professional: true,
          companyID: currentUser.company_id,
          agencyID: currentUser.agency_id,
          pro_type: professionalType,
          professionalType,
        },
        {
          integrations: { Intercom: true },
          Intercom: { user_hash: currentUser.user_hash },
        },
      );

      analytics.track('Professional Login', {
        Date: moment().format('MM/DD/YYYY'),
        OperatingSystem: deviceInfo.current.operatingSystem,
        OperatingSystemVersion: deviceInfo.current.operatingSystemVersion,
        BrowserVersion: deviceInfo.current.browserVersion,
        Device: deviceInfo.current.device,
        pro_type: professionalType,
      });
    }
  };
  useEffect(identifyUser, [currentUser?.id]);

  useEffect(() => {
    if (!currentUser) {
      return;
    }
    const loginPath = `${config.root}/login`;
    if (location.pathname === loginPath || location.pathname === `${loginPath}/`) {
      navigate(config.root || '/', { replace: true });
    }
  }, [config.root, currentUser, location.pathname, navigate]);

  if (webView) {
    return <WebViews />;
  }

  if (!!urlHasMobileAuthToken) {
    return <MobileAuthLogin />;
  }

  if (loadingUser || isLoadingStatsig) {
    return <CenteredLoader />;
  }

  if (!currentUser) {
    return (
      <Suspense fallback={<CenteredLoader />}>
        <Head>
          {/* default so the previous page title isn't displayed when the current page title is undefined */}
          <title>{pageTitles.default}</title>
        </Head>
        <Routes>
          <Route path={`${config.root}/login`} element={<Login />} />
          <Route path={`${config.root}/mfa-entry`} element={<MfaSmsCodeEntryScreen />} />
          <Route
            path={`${config.root}/mfa-verification`}
            element={<MfaIdentityVerificationScreen />}
          />
          <Route path={`${config.root}/invite`} element={<AcceptInvite />} />
          <Route path={`${config.root}/sign-up`} element={<SignUp />} />
          <Route path={`${config.root}/create`} element={<Create />} />
          <Route path={`${config.root}/set-password`} element={<SetPassword />} />
          <Route path={`${config.root}/reset-password`} element={<ResetPassword />} />
          <Route path={`${config.root}/reference-gateway/*`} element={<ReferenceGateway />} />
          <Route
            path={`${config.root}/assignments_boards/:id`}
            element={<LocationRedirectWrapper host={config.landingsUrl} />}
          />
          <Route
            path="*"
            element={<Navigate to={{ pathname: `${config.root}/login`, search: search }} replace />}
          />
        </Routes>
      </Suspense>
    );
  }

  const firebaseUser = firebaseAuth?.currentUser;
  const mfaInfo = firebaseUser ? multiFactor(firebaseUser) : null;
  const userEnrolledInMfa = mfaInfo ? mfaInfo.enrolledFactors.length > 0 : false;

  return (
    <GooglePlacesLoaderProvider apiKey={configKeys.googleMapsKey}>
      <ProView>
        <Suspense fallback={<CenteredLoader />}>
          <Head>
            {/* default so the previous page title isn't displayed when the current page title is undefined */}
            <title>{pageTitles.default}</title>
          </Head>
          <Routes>
            <Route path={`${config.root}/mfa-entry`} element={<MfaSmsCodeEntryScreen />} />
            {!userEnrolledInMfa && (
              <Route
                path={`${config.root}/mfa-verification`}
                element={<MfaIdentityVerificationScreen />}
              />
            )}
            <Route path={`${config.root}/pro-pool/*`} element={<ProPoolRoot />} />
            <Route path={`${config.root}/application/*`} element={<ApplicationRoot />} />
            <Route path={`${config.root}/assignments/*`} element={<AssignmentsRoot />} />
            <Route path={`${config.root}/available/*`} element={<AvailableRoot />} />
            <Route path={`${config.root}/credentials/*`} element={<CredentialsRoot />} />
            <Route path={`${config.root}/my-assignments/*`} element={<MyAssignmentsRoot />} />
            <Route path={`${config.root}/earnings/*`} element={<EarningsRoot />} />
            <Route
              path={`${config.root}/jobs/assignments`}
              element={
                <Navigate
                  to={{
                    pathname: `${config.root}/available/assignments/local`,
                    search: url.search,
                  }}
                  replace
                />
              }
            />
            <Route
              path={`${config.root}/available/local`}
              element={
                <Navigate
                  to={{ pathname: `${config.root}/available/jobs`, search: url.search }}
                  replace
                />
              }
            />
            <Route
              path={`${config.root}/jobs/available`}
              element={
                <Navigate
                  to={{ pathname: `${config.root}/available/jobs`, search: url.search }}
                  replace
                />
              }
            />
            <Route path={`${config.root}/jobs/*`} element={<JobsRoot />} />
            <Route path={`${config.root}/profile/*`} element={<ProfileRoot />} />
            <Route path={`${config.root}/schedule/*`} element={<ScheduleRoot />} />
            <Route
              path={`${config.root}/timesheets/*`}
              element={
                <TimesheetsRouteWrapper currentUser={currentUser}>
                  <TimecardsRoot />
                </TimesheetsRouteWrapper>
              }
            />
            <Route path={`${config.root}/settings/*`} element={<SettingsRoot />} />
            <Route path={`${config.root}/onboarding/*`} element={<OnboardingRoot />} />
            <Route path={`${config.root}/shift-swaps/*`} element={<ShiftSwapsRoot />} />
            <Route
              path={`${config.root}/assignments_boards/:id`}
              element={<LocationRedirectWrapper host={config.landingsUrl} />}
            />
            <Route
              path={`${config.root}/dashboard/credentials`}
              element={
                <Navigate
                  to={!isApplicant ? `${config.root}/credentials` : `${config.root}/application`}
                  replace
                />
              }
            />
            <Route path={`${config.root}/dashboard/*`} element={<Navigate to="/" replace />} />
            <Route
              path={`${config.root}/dashboard`}
              element={
                <Navigate
                  to={
                    !isApplicant
                      ? `${config.root}/my-assignments/applications`
                      : `${config.root}/application`
                  }
                  replace
                />
              }
            />
            <Route
              path={`${config.root}/jobs-dashboard`}
              element={<Navigate to={`${config.root}/my-assignments/applications`} replace />}
            />
            {!isApplicant && isSaasCredentialingAccount(currentUser) && (
              <Route
                path={config.root}
                element={<Navigate to={`${config.root}/credentials`} replace />}
              />
            )}
            <Route path={`${config.root}/*`} element={<TodayRoot />} />
            <Route path="*" element={<Navigate to={config.root} replace />} />
          </Routes>
        </Suspense>
      </ProView>
    </GooglePlacesLoaderProvider>
  );
};

export default AppRouter;
